/**
 * Shared loading / empty / error state blocks (P30 / PENDING_TODO A5.7).
 *
 * Every API-backed surface eventually has 4 states: loading, empty, error,
 * happy-path. Until P30 the wireframe only rendered the happy path. These
 * tiny components give us a uniform vocabulary for the other three so we
 * stop pasting `<p className="subtle">` everywhere.
 */
import type { JSX, ReactNode } from 'react';
import { AlertTriangle, Inbox, Loader2 } from 'lucide-react';

interface BaseProps {
  readonly title?: string;
  readonly hint?: ReactNode;
  readonly compact?: boolean;
}

const wrapStyle = (compact: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: compact ? '12px 0' : '24px 16px',
  color: 'var(--muted-foreground, #888)',
  fontSize: 15,
});

const iconStyle = { flex: '0 0 auto', opacity: 0.7 } as const;

export function LoadingState({ title = 'Loading…', hint, compact }: BaseProps): JSX.Element {
  return (
    <div role="status" aria-live="polite" style={wrapStyle(Boolean(compact))}>
      <Loader2
        size={18}
        style={{ ...iconStyle, animation: 'spin 1s linear infinite' }}
        aria-hidden
      />
      <div>
        <strong style={{ display: 'block', color: 'var(--foreground)' }}>{title}</strong>
        {hint && <span>{hint}</span>}
      </div>
    </div>
  );
}

export function EmptyState({ title = 'Nothing here yet', hint, compact }: BaseProps): JSX.Element {
  return (
    <div role="status" style={wrapStyle(Boolean(compact))}>
      <Inbox size={18} style={iconStyle} aria-hidden />
      <div>
        <strong style={{ display: 'block', color: 'var(--foreground)' }}>{title}</strong>
        {hint && <span>{hint}</span>}
      </div>
    </div>
  );
}

export interface ErrorStateProps extends BaseProps {
  readonly error?: unknown;
}

export function ErrorState({
  title = 'Could not load',
  hint,
  error,
  compact,
}: ErrorStateProps): JSX.Element {
  const message = error instanceof Error ? error.message : error ? String(error) : null;
  return (
    <div
      role="alert"
      style={{ ...wrapStyle(Boolean(compact)), color: 'var(--destructive, #ff8888)' }}
    >
      <AlertTriangle size={18} style={iconStyle} aria-hidden />
      <div>
        <strong style={{ display: 'block' }}>{title}</strong>
        {hint && <span>{hint}</span>}
        {message && (
          <code style={{ display: 'block', fontSize: 13, marginTop: 4, opacity: 0.8 }}>
            {message}
          </code>
        )}
      </div>
    </div>
  );
}

/** Convenience: pick one of the three based on a TanStack-Query-style result. */
export interface AsyncShellProps {
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly isEmpty: boolean;
  readonly error?: unknown;
  readonly loadingTitle?: string;
  readonly emptyTitle?: string;
  readonly emptyHint?: ReactNode;
  readonly errorTitle?: string;
  readonly errorHint?: ReactNode;
  readonly compact?: boolean;
  readonly children: ReactNode;
}

export function AsyncShell(props: AsyncShellProps): JSX.Element {
  if (props.isLoading) {
    return <LoadingState title={props.loadingTitle} compact={props.compact} />;
  }
  if (props.isError) {
    return (
      <ErrorState
        title={props.errorTitle}
        hint={props.errorHint}
        error={props.error}
        compact={props.compact}
      />
    );
  }
  if (props.isEmpty) {
    return <EmptyState title={props.emptyTitle} hint={props.emptyHint} compact={props.compact} />;
  }
  return <>{props.children}</>;
}
